import type { GeneratorOptions, Schema } from '@cli-types/core-engine/schema.js'
import type { FormGeneratorResult } from '@cli-types/next-adapter/forms.js'
import { createGeneratedFile } from '@cli-utils/files.js'
import { pluralize, singularize, toKebabCase, toPascalCase } from '@cli-utils/text.js'
import { flattenFields } from '@core-engine/schema/field-helpers/flattening/flatten-fields.js'
import { hasFieldType } from '@core-engine/schema/field-helpers/walking/has-field-type.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths/resolve-schema-action-import-path.js'
import { isAuthorshipFieldName } from '@next-adapter/generators/actions/action-helpers/is-authorship-field-name.js'
import { buildUiImports } from '@next-adapter/generators/form/form-shared/build-ui-imports.js'
import { collectTabIconNames } from '@next-adapter/generators/form/form-shared/collect-tab-icon-names.js'
import { hasSimpleListField } from '@next-adapter/generators/form/form-shared/has-simple-list-field.js'
import { renderSingleFormRenderer } from '@next-adapter/templates/renderers/generators/form/form-single/generate-single-form.js'
import { analyzeGroup } from './analyze-group.js'
import { generateCardComponent } from './generate-card-component.js'
import { hasRenderedIconPostfix } from './has-rendered-icon-postfix.js'
import { parseCardGroups } from './parse-card-groups.js'

export function generateSingleForm(
  schema: Schema,
  pagesDir: string,
  options: GeneratorOptions = {}
): FormGeneratorResult {
  const actionImportPath = resolveSchemaActionImportPath(schema.name, options.outputNamespace)
  const singular = singularize(schema.name)
  const Singular = toPascalCase(singular)

  const allFormFields = schema.fields.filter(
    (f) =>
      !f.primaryKey &&
      f.name !== 'createdAt' &&
      f.name !== 'updatedAt' &&
      !isAuthorshipFieldName(f.name)
  )

  const cardGroups = parseCardGroups(allFormFields, schema.label)

  const flatFields = flattenFields(allFormFields)
  const inputFreeFieldTypes = new Set([
    'boolean',
    'image',
    'video',
    'media',
    'gallery',
    'icon',
    'date',
    'select',
    'richtext',
    'markdown',
    'text',
    'relationship',
    'list',
    'separator',
    'tabs'
  ])
  const formControlFreeFieldTypes = new Set(['separator', 'tabs'])

  const cardArtifacts = cardGroups.map((group) => {
    const analysis = analyzeGroup(group.fields)
    const groupFlatFields = group.flatFields
    const creatableSelectFields = groupFlatFields.filter(
      (field) => field.type === 'select' && field.creatable === true
    )
    const hasCreatableSelect = creatableSelectFields.length > 0
    const hasMultiSelect = analysis.multiSelectFields.length > 0
    const hasRelationship = analysis.relFields.length > 0
    const hasSelectCombobox = hasRelationship || hasMultiSelect || hasCreatableSelect
    const hasSelect = groupFlatFields.some(
      (field) => field.type === 'select' && field.multiple !== true && field.creatable !== true
    )
    const hasIconPostfix = hasRenderedIconPostfix(group.fields)
    const hasNestedList = analysis.listFieldsWithNested.length > 0
    const hasInput =
      analysis.listFieldsWithNested.some((field) =>
        field.fields?.some(
          (nestedField) => !['boolean', 'image', 'video', 'media'].includes(nestedField.type)
        )
      ) ||
      groupFlatFields.some(
        (field) => !field.primaryKey && !field.hidden && !inputFreeFieldTypes.has(field.type)
      )
    const hasFormControls =
      groupFlatFields.some((field) => {
        if (field.primaryKey || field.hidden) return false
        if (field.hasIcon && !['group', 'section', 'tabs', 'separator'].includes(field.type)) {
          return true
        }
        if (field.type === 'list' && (!field.fields || field.fields.length === 0)) return false
        return !formControlFreeFieldTypes.has(field.type)
      }) || hasIconPostfix

    const groupUiImports = buildUiImports({
      hasBoolean: hasFieldType(group.fields, 'boolean'),
      hasTextarea: hasFieldType(group.fields, 'text'),
      hasImage: hasFieldType(group.fields, 'image'),
      hasVideo: hasFieldType(group.fields, 'video'),
      hasMedia: hasFieldType(group.fields, 'media'),
      hasGallery: hasFieldType(group.fields, 'gallery'),
      hasIcon: hasFieldType(group.fields, 'icon'),
      hasIconPostfix,
      hasDate: hasFieldType(group.fields, 'date'),
      hasMarkdown: hasFieldType(group.fields, 'markdown'),
      hasRichtext: hasFieldType(group.fields, 'richtext'),
      hasSeparator: hasFieldType(group.fields, 'separator'),
      hasSelect,
      hasSelectCombobox,
      hasCreatableSelect,
      hasMultiSelect,
      hasTabsField: analysis.hasTabsField,
      hasRelationship,
      hasSimpleList: hasSimpleListField(group.fields),
      hasNestedList,
      hasNestedObjectList: analysis.hasNestedObjectList,
      hasCardContent: true,
      hasSectionCardHeading: true,
      hasFormRoot: true,
      hasButton: true,
      hasFormControls,
      hasInput
    })
    const cardImportIndex = groupUiImports.findIndex((value) =>
      value.includes("from '@admin/components/ui/card'")
    )
    if (cardImportIndex !== -1) {
      const cardImportNames = [
        'Card',
        'CardContent',
        ...(group.description ? ['CardDescription'] : []),
        'CardFooter',
        'CardHeader',
        'CardTitle'
      ]
      groupUiImports[cardImportIndex] = renderSingleFormRenderer({
        kind: 'renderSingleFormImportsTemplate',
        props: {
          map: cardImportNames.map((name) => `  ${name}`).join(',\n')
        }
      })
    }
    groupUiImports.push("import { Spinner } from '@admin/components/ui/spinner'")

    const lucideIcons: string[] = []
    const addLucideIcons = (...icons: string[]) => {
      for (const icon of icons) {
        if (!lucideIcons.includes(icon)) lucideIcons.push(icon)
      }
    }
    addLucideIcons(...collectTabIconNames(group.fields))
    if (hasSelectCombobox) addLucideIcons('Check', 'ChevronsUpDown')
    if (hasCreatableSelect || hasNestedList) addLucideIcons('Plus')
    if (hasNestedList) addLucideIcons('X')

    const relationshipImports = Array.from(
      new Set(analysis.relFields.map((field) => field.relationship).filter(Boolean))
    )
      .map((relationship) => {
        const relationshipPlural = toPascalCase(pluralize(relationship || ''))
        return `import { use${relationshipPlural} } from '@admin/hooks/use-${relationship}'`
      })
      .join('\n')
    const needsReact =
      hasRelationship ||
      hasMultiSelect ||
      hasCreatableSelect ||
      analysis.listFieldsWithNested.length > 0
    const hookImports = ['useForm']
    if (hasNestedList) hookImports.unshift('useFieldArray')
    const actionTypeImports = [
      Singular,
      ...(hasCreatableSelect ? [`${Singular}SelectOption`] : []),
      `${Singular}UpsertInput`
    ]
    const actionImports = [
      ...(hasCreatableSelect
        ? [`create${Singular}SelectOption`, `get${Singular}SelectOptions`]
        : []),
      `upsert${Singular}`
    ]
    const cardImports = [
      needsReact ? "import * as React from 'react'" : '',
      "import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'",
      `import { useMutation, ${hasCreatableSelect ? 'useQuery, ' : ''}useQueryClient } from '@tanstack/react-query'`,
      lucideIcons.length > 0 ? `import { ${lucideIcons.join(', ')} } from 'lucide-react'` : '',
      `import { ${hookImports.join(', ')} } from 'react-hook-form'`,
      "import { toast } from 'sonner'",
      "import { z } from 'zod/v3'",
      analysis.hasTabsField ? "import { parseAsStringLiteral, useQueryState } from 'nuqs'" : '',
      hasSelectCombobox ? "import { cn } from '@admin/utils/shared/cn'" : '',
      relationshipImports,
      groupUiImports.join('\n'),
      `import type { ${actionTypeImports.join(',\n  ')} } from '@admin/actions/${actionImportPath}'`,
      `import { ${actionImports.join(', ')} } from '@admin/actions/${actionImportPath}'`
    ]
      .filter(Boolean)
      .join('\n')
    const content = renderSingleFormRenderer({
      kind: 'renderSingleFormCardModuleTemplate',
      props: {
        imports: cardImports,
        content: generateCardComponent(group, schema, analysis, flatFields)
      }
    })

    return {
      content,
      fileName: `${toKebabCase(group.componentName)}.tsx`,
      group
    }
  })

  const cardRenders = cardArtifacts
    .map(({ group }) => `        <${group.componentName} initialData={initialData} />`)
    .join('\n')

  const content = renderSingleFormRenderer({
    kind: 'renderSingleFormEntryModuleTemplate',
    props: {
      singular: Singular,
      actionImportPath: actionImportPath,
      cardImports: cardArtifacts
        .map(
          ({ fileName, group }) =>
            `import { ${group.componentName} } from './${fileName.slice(0, -4)}'`
        )
        .join('\n'),
      cardRenders
    }
  })

  return {
    files: [
      createGeneratedFile(`${pagesDir}/${schema.name}/${schema.name}-form.tsx`, content),
      ...cardArtifacts.map(({ content: cardContent, fileName }) =>
        createGeneratedFile(`${pagesDir}/${schema.name}/${fileName}`, cardContent)
      )
    ]
  }
}
